import { useState, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';

import {
  TIME_RANGE_PRESETS,
  TimeRangePresetId,
  TimeRangeValue,
  buildCustomTimeRange,
  buildTimeRange,
  getTimeRangeLabel,
  resolveTimeRange,
  toDayString,
} from './time-range.utils';

export interface TimeRangeFilterProps {
  value: TimeRangeValue;
  onChange: (value: TimeRangeValue) => void;
  // Name of the date column the range applies to, e.g. "Order Date".
  columnLabel: string;
  // Preset the Reset action falls back to (the table's own default).
  defaultPreset?: Exclude<TimeRangePresetId, 'custom'>;
}

export default function TimeRangeFilter(props: TimeRangeFilterProps) {
  const {
    value,
    onChange,
    columnLabel,
    defaultPreset = 'all',
  } = props;

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const close = () => setAnchorEl(null);

  // Picking a preset is a complete choice, so it applies and dismisses. The
  // custom from/to inputs stay open — those need both bounds before they mean
  // anything — and close via Done.
  const selectPreset = (preset: Exclude<TimeRangePresetId, 'custom'>) => {
    onChange(buildTimeRange(preset));
    close();
  };

  // A named preset stores no bounds, so show the days it currently resolves to
  // — switching to a custom range then starts from what the user can see.
  const resolved = resolveTimeRange(value);
  const fromValue = resolved.from ? toDayString(resolved.from) : '';
  const toValue = resolved.to ? toDayString(resolved.to) : '';

  const setCustom = (patch: { from?: string; to?: string }) => {
    onChange(buildCustomTimeRange(
      patch.from ?? fromValue,
      patch.to ?? toValue,
    ));
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        startIcon={<DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        fullWidth={isSmall}
        sx={{
          height: 41,
          textTransform: 'none',
          justifyContent: isSmall ? 'flex-start' : 'center',
          borderColor: 'rgba(0, 0, 0, 0.23)',
          color: 'text.primary',
          bgcolor: 'background.paper',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: 'brand_gold.main', bgcolor: 'background.paper' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
          <Box
            component="span"
            sx={{
              fontSize: '0.6875rem',
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {columnLabel}
          </Box>
          <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            {getTimeRangeLabel(value)}
          </Box>
        </Box>
      </Button>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: 'min(300px, calc(100vw - 32px))',
              py: 1,
            },
          },
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: 'block', px: 2, pt: 0.5, pb: 1, color: 'text.secondary', lineHeight: 1.4 }}
        >
          Time range
        </Typography>

        {
          TIME_RANGE_PRESETS.map((preset) => {
            const active = value.preset === preset.id;
            return (
              <Box
                key={preset.id}
                role="menuitem"
                tabIndex={0}
                onClick={() => selectPreset(preset.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectPreset(preset.id);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1.125,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  color: active ? 'primary.main' : 'text.primary',
                  fontWeight: active ? 500 : 400,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <CheckIcon
                  fontSize="small"
                  sx={{ fontSize: 18, color: active ? 'primary.main' : 'transparent' }}
                />
                <span>{preset.label}</span>
              </Box>
            );
          })
        }

        <Divider sx={{ mt: 1 }} />

        <Stack spacing={1.25} sx={{ px: 2, pt: 1.75, pb: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
            Custom range
          </Typography>
          <Stack direction="row" spacing={1.25}>
            {/* max/min keep the bounds from crossing — an inverted range would
                return nothing while the empty state advised widening it. */}
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              value={fromValue}
              onChange={(event) => setCustom({ from: event.target.value })}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: toValue ? { max: toValue } : {},
              }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              value={toValue}
              onChange={(event) => setCustom({ to: event.target.value })}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: fromValue ? { min: fromValue } : {},
              }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', px: 1.5, pb: 0.5 }}>
          <Button
            size="small"
            onClick={() => selectPreset(defaultPreset)}
          >
            Reset
          </Button>
          <Button size="small" variant="contained" onClick={close}>
            Done
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
